import * as THREE from 'three';

export type ProjectileOwner = 'player' | 'enemy';

export interface Projectile {
  active: boolean;
  owner: ProjectileOwner;
  mesh: THREE.Mesh;
  position: THREE.Vector3;
  velocity: THREE.Vector3;
  /** Captured at fire time (PLAN-CITY §0.7) — never re-derived when the projectile resolves a hit. */
  damage: number;
  knockbackForce: number;
  radius: number;
  life: number;
  /** Downward acceleration magnitude — 0 for The Nine's straight bullets, >0 for the trash-can lobber's arc. */
  gravity: number;
  /** Fire-time-captured (§0.7) — the FINAL crit-applied damage is already baked into `damage` above; this flag is purely cosmetic (a brighter tint on hit), never re-read to recompute damage. */
  crit: boolean;
  /** Player-owned only: whether this projectile has already credited a graze this flight (`combat/graze.ts`) — an enemy lob sits inside the graze band for several ticks as it passes, and must only ever count once. */
  grazed: boolean;
  /** K-1(a): true for an ENEMY projectile the player can destroy in flight before it deals damage (Gridlock's hubcap, so far) — undefined/false for every other projectile, player or enemy, so this is fully additive. Reset explicitly on every `fire()` call since pool slots are reused across unrelated projectiles. */
  breakable: boolean;
  /** Remaining hit points before a `breakable` projectile is destroyed by player fire — meaningless when `breakable` is false. */
  hp: number;
}

interface FireOptions {
  owner: ProjectileOwner;
  origin: THREE.Vector3;
  velocity: THREE.Vector3;
  damage: number;
  knockbackForce: number;
  radius: number;
  life: number;
  gravity?: number;
  color: number;
  crit?: boolean;
  /** K-1(a): see `Projectile.breakable`'s own comment. Defaults to false/0 for every existing caller. */
  breakable?: boolean;
  hp?: number;
}

const MAX_PROJECTILES = 48; // pool cap — §3's "pool everything" budget discipline, well inside the FX draw-call budget

/**
 * A flat pool shared by both player bullets and enemy lobs — one array, one
 * update loop, `owner` distinguishes which side's targets a hit-test should
 * use. Pooled (never allocated per shot) per PLAN-CITY §3.
 */
export class ProjectilePool {
  readonly group = new THREE.Group();
  private readonly pool: Projectile[] = [];
  private readonly geo = new THREE.SphereGeometry(0.1, 8, 6);

  constructor() {
    for (let i = 0; i < MAX_PROJECTILES; i++) {
      const mat = new THREE.MeshBasicMaterial({ color: 0xffffff });
      const mesh = new THREE.Mesh(this.geo, mat);
      mesh.visible = false;
      this.group.add(mesh);
      this.pool.push({
        active: false,
        owner: 'player',
        mesh,
        position: new THREE.Vector3(),
        velocity: new THREE.Vector3(),
        damage: 0,
        knockbackForce: 0,
        radius: 0.1,
        life: 0,
        gravity: 0,
        crit: false,
        grazed: false,
        breakable: false,
        hp: 0,
      });
    }
  }

  /** K-1(a): returns the fired `Projectile` (or `null` if the pool was exhausted) so a caller that needs to track a SPECIFIC shot across future ticks (Gridlock's hubcap — main.ts polls `.active` to know when it resolves) can do so. Every pre-K1 call site already ignored the return value, so this is additive. */
  fire(opts: FireOptions): Projectile | null {
    const p = this.pool.find((slot) => !slot.active);
    if (!p) return null; // pool exhausted — drop the shot silently rather than growing unbounded
    p.active = true;
    p.owner = opts.owner;
    p.position.copy(opts.origin);
    p.velocity.copy(opts.velocity);
    p.damage = opts.damage;
    p.knockbackForce = opts.knockbackForce;
    p.radius = opts.radius;
    p.life = opts.life;
    p.gravity = opts.gravity ?? 0;
    p.crit = opts.crit ?? false;
    p.grazed = false;
    p.breakable = opts.breakable ?? false;
    p.hp = opts.hp ?? 0;
    p.mesh.visible = true;
    p.mesh.position.copy(p.position);
    (p.mesh.material as THREE.MeshBasicMaterial).color.setHex(p.crit ? 0xffffff : opts.color);
    return p;
  }

  /** Advances every active projectile; returns the still-active ones for the caller's own hit-testing. */
  update(dt: number): Projectile[] {
    const active: Projectile[] = [];
    for (const p of this.pool) {
      if (!p.active) continue;
      p.velocity.y -= p.gravity * dt;
      p.position.addScaledVector(p.velocity, dt);
      p.life -= dt;
      if (p.life <= 0 || p.position.y < -2) {
        this.deactivate(p);
        continue;
      }
      p.mesh.position.copy(p.position);
      active.push(p);
    }
    return active;
  }

  deactivate(p: Projectile): void {
    p.active = false;
    p.mesh.visible = false;
  }

  activeCount(): number {
    let n = 0;
    for (const p of this.pool) if (p.active) n++;
    return n;
  }

  /** Same "expose the live pool for inspection" convention `ai/spawner.ts`'s `EnemyManager.getEnemies()` already uses — dev/harness only (e.g. verifying `crit` was actually baked at fire time), never read by real gameplay code. */
  getActive(): readonly Projectile[] {
    return this.pool.filter((p) => p.active);
  }

  /** Deactivates every currently-active projectile — a clean-slate reset. Not used by any real gameplay path; exposed for the dev/harness hook so a check that wants an isolated combat measurement can clear stray in-flight shots left over from an earlier, unrelated window instead of them silently scoring a hit mid-check. */
  clearAll(): void {
    for (const p of this.pool) if (p.active) this.deactivate(p);
  }
}
